import { useCallback, useEffect } from 'react';
import { PDF_OBSERVER_ROOT_MARGIN, PDF_OBSERVER_THRESHOLD } from '@/constants/pdf';
import { createPlaceholderPageContainer } from './pageElements';
import { renderScrollPage, renderSinglePage } from './pageRenderers';
import type { PdfDisplayMode } from './types';
import type { PdfViewerState } from './usePdfViewerState';

interface UsePdfPageRenderArgs {
  state: PdfViewerState;
  displayMode: PdfDisplayMode;
  lazyLoadBuffer: number;
  initialPage: number;
  scrollToPage: (page: number, behavior?: ScrollBehavior, block?: 'start' | 'center') => void;
}

/** Paints pages onto canvases and drives lazy loading in scroll mode. */
export function usePdfPageRender({
  state,
  displayMode,
  lazyLoadBuffer,
  initialPage,
  scrollToPage,
}: UsePdfPageRenderArgs) {
  const {
    pdfDoc, totalPages, scale, currentPage, visiblePages, renderedPages, pageRenderComplete,
    setVisiblePages, setRenderedPages, setPageRenderComplete,
    containerRef, pageContainerRefs, observerRef,
    renderedPagesRef, renderingPagesRef, pendingInitialScrollPageRef,
  } = state;

  const renderScroll = useCallback(async (pageNum: number, shouldContinue: () => boolean) => {
    if (!pdfDoc) return;
    await renderScrollPage({ pdfDoc, pageNum, scale, state, shouldContinue });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state is a stable container of refs and setters
  }, [pdfDoc, scale]);

  // Scroll mode: build every page placeholder, then observe them for lazy loading.
  useEffect(() => {
    if (displayMode !== 'scroll' || !pdfDoc || !containerRef.current || totalPages === 0) return;

    let isCancelled = false;

    const currentPageContainerRefs = pageContainerRefs.current;
    const currentRenderedPages = renderedPagesRef.current;
    const currentRenderingPages = renderingPagesRef.current;

    const container = containerRef.current;
    container.innerHTML = '';

    (async () => {
      const firstPage = await pdfDoc.getPage(1);
      if (isCancelled) return;

      const viewport = firstPage.getViewport({ scale });

      for (let i = 1; i <= totalPages; i++) {
        const pageContainer = createPlaceholderPageContainer(i, viewport.width, viewport.height);
        container.appendChild(pageContainer);
        pageContainerRefs.current.set(i, pageContainer);
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const newVisiblePages = new Set<number>();

          entries.forEach((entry) => {
            const pageNum = parseInt(entry.target.getAttribute('data-page') || '0', 10);
            if (entry.isIntersecting) {
              newVisiblePages.add(pageNum);
            }
          });

          setVisiblePages((prev) => {
            const updated = new Set(prev);
            newVisiblePages.forEach((p) => {
              for (
                let i = Math.max(1, p - lazyLoadBuffer);
                i <= Math.min(totalPages, p + lazyLoadBuffer);
                i++
              ) {
                updated.add(i);
              }
            });
            return updated;
          });
        },
        {
          root: container,
          rootMargin: PDF_OBSERVER_ROOT_MARGIN,
          threshold: PDF_OBSERVER_THRESHOLD,
        },
      );

      observerRef.current = observer;

      pageContainerRefs.current.forEach((pageContainer) => {
        observer.observe(pageContainer);
      });

      if (initialPage > 1 && initialPage <= totalPages) {
        pendingInitialScrollPageRef.current = initialPage;
        scrollToPage(initialPage, 'auto', 'start');
      }

      setPageRenderComplete(true);
    })();

    return () => {
      isCancelled = true;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      currentPageContainerRefs.clear();
      currentRenderedPages.clear();
      currentRenderingPages.clear();
      setRenderedPages(new Set());
      setVisiblePages(new Set());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and setters are stable
  }, [displayMode, pdfDoc, totalPages, scale, lazyLoadBuffer, initialPage, scrollToPage]);

  // Scroll mode: render pages as they become visible.
  useEffect(() => {
    if (displayMode !== 'scroll' || !pdfDoc) return;

    let isCancelled = false;
    const shouldContinue = () => !isCancelled;

    (async () => {
      const pagesToRender = Array.from(visiblePages)
        .filter(
          p =>
            !renderedPagesRef.current.has(p) &&
            !renderingPagesRef.current.has(p),
        )
        .sort((a, b) => a - b);

      for (const pageNum of pagesToRender) {
        if (isCancelled) break;
        await renderScroll(pageNum, shouldContinue);
      }
    })();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
  }, [displayMode, pdfDoc, visiblePages, renderScroll]);

  // The first scroll runs against placeholders; repeat it once the real page is
  // painted, because its height may differ from the estimate.
  useEffect(() => {
    if (displayMode !== 'scroll' || !pageRenderComplete) return;

    const pendingPage = pendingInitialScrollPageRef.current;
    if (!pendingPage || !renderedPages.has(pendingPage)) return;

    scrollToPage(pendingPage, 'auto', 'start');
    pendingInitialScrollPageRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
  }, [displayMode, pageRenderComplete, renderedPages, scrollToPage]);

  // Single page mode: render the current page whenever it changes.
  useEffect(() => {
    if (displayMode !== 'single') return;

    let isCancelled = false;
    const shouldContinue = () => !isCancelled;

    if (pdfDoc) {
      setPageRenderComplete(false);

      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      (async () => {
        await renderSinglePage({ pdfDoc, pageNum: currentPage, scale, state, shouldContinue });
        if (!isCancelled) {
          setPageRenderComplete(true);
        }
      })();
    }

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state is a stable container of refs and setters
  }, [displayMode, pdfDoc, currentPage, scale]);
}
